/**
 * My Account → marketing email preference (B8).
 *
 * Editable in BOTH directions. Every change appends a new consent entry — an
 * opt-out is a withdrawal record, never an erasure of the earlier opt-in — and
 * pushes the Resend mirror (audience contact + opt-out-by-default topic).
 */
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Mail, Loader2 } from "lucide-react";
import {
  getMyMarketingConsent,
  setMarketingPreference,
  type MarketingConsentState,
} from "@/services/marketing-consent";

const UserMarketingPreference = () => {
  const { toast } = useToast();
  const [state, setState] = useState<MarketingConsentState | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getMyMarketingConsent().then((s) => {
      if (!cancelled) setState(s);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggle = async (next: boolean) => {
    setSaving(true);
    const { error } = await setMarketingPreference(next);
    setSaving(false);
    if (error) {
      toast({
        title: "Couldn't save that",
        description: error.message,
        variant: "destructive",
      });
      return;
    }
    setState({ consented: next, known: true, recordedAt: new Date().toISOString() });
    toast({
      title: next ? "You're subscribed" : "You're unsubscribed",
      description: next
        ? "We'll email you about new styles and features."
        : "We'll only email you about your own orders from now on.",
    });
  };

  const consented = state?.consented === true;

  return (
    <Card className="shadow-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Mail className="h-4 w-4" /> Email preferences
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <Label className="text-sm" htmlFor="marketing-opt-in">
              Cardmoji news and offers
            </Label>
            <p className="text-xs text-muted-foreground leading-relaxed">
              New styles, features and offers. Turning this off never affects
              emails about your own order, payments or account security.
            </p>
          </div>
          {state === null ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground mt-1" />
          ) : (
            <Switch
              id="marketing-opt-in"
              checked={consented}
              disabled={saving}
              onCheckedChange={toggle}
              data-testid="account-marketing-toggle"
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default UserMarketingPreference;
